import React from 'react';
import { Card, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { CustomTheme } from '../../appTemplates/MUIProviderTemplate/MUIProviderTemplateInterfaces';
import { APP_ERRORS } from '../../constants/backendResponse';
import { RootStore } from '../../store/reducers';
import { getTranslation } from '../../utils/functions';
import sanitizer from '../../services/sanitize-service';
import {
  appErrorSelector,
  languageSelector,
  userAlreadyAvailableFormPathSelector,
} from '../../store/selectors/appSelectors';
import {
  textsOverwritingsSelector,
  quotaExceededMessageSelector,
} from '../../store/selectors/globalConfigSelectors';
import AppErrorPage from '../../views/AppErrorPage/AppErrorPage';
import Link from '@hid-galaxy-ui/galaxy-react/components/Link';
import { FORM_SESSION_TOKEN_PARAM } from '../../constants/formParams';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';
import Typography from '@hid-galaxy-ui/galaxy-react/components/Typography';
import { PATHS_WITH_PARAMS } from '../../utils/paths';

const useMainContentWrapperStyles = makeStyles((theme: CustomTheme) => ({
  cardClass: {
    [theme.breakpoints.up('xs')]: {
      marginTop: '40px',
      padding: '40px 60px',
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: '0px',
      padding: '24px 20px 60px 20px',
    },
  },
}));

const AppError: React.FC = () => {
  const { appError, textsOverwritings, userAlreadyAvailableFormPath } =
    useSelector((state: RootStore) => ({
      appError: appErrorSelector(state),
      textsOverwritings: textsOverwritingsSelector(state),
      userAlreadyAvailableFormPath: userAlreadyAvailableFormPathSelector(state),
    }));

  const { cardClass } = useMainContentWrapperStyles();

  const getFormSessionSubmittedMessage = (): React.ReactNode => {
    const text: string = getTranslation(
      textsOverwritings ?? {},
      'FORM_SESSION_SUBMITTED_MESSAGE',
    );
    return (
      <Card className={cardClass}>
        <section>
          <Typography
            variant={TypographyVariantEnum.H3}
            className="hid-spacing__mt-05 hid-flex hid-flex-row hid-flex-jc-center"
          >
            {text || getTranslation('FORM_SESSION_SUBMITTED_MESSAGE')}
          </Typography>
          <div className="hid-flex hid-flex-row hid-flex-jc-center hid-spacing__mt-05">
            {renderGoBackLink()}
          </div>
        </section>
      </Card>
    );
  };

  const getFormSessionPendingMessage = (): React.ReactNode => {
    const text: string = getTranslation('FORM_SESSION_PENDING_MESSAGE');

    return (
      <Card className={cardClass}>
        <section>
          <Typography
            variant={TypographyVariantEnum.H3}
            className="hid-spacing__mt-05"
          >
            {text}
          </Typography>
          <div className="hid-flex hid-flex-row hid-flex-jc-center hid-spacing__mt-05">
            {renderPendingFormLink()}
          </div>
        </section>
      </Card>
    );
  };

  const renderGoBackLink = (): React.ReactNode => {
    return (
      <Link target="_self" link={`${PATHS_WITH_PARAMS.my_forms}`}>
        {getTranslation('GO_BACK')}
      </Link>
    );
  };

  const renderPendingFormLink = (): React.ReactNode => {
    return (
      <Link target="_self" link={`${userAlreadyAvailableFormPath}`}>
        {getTranslation('RESUME_FORM')}
      </Link>
    );
  };

  let message: string | React.ReactNode;

  switch (appError) {
    case APP_ERRORS.FORM_SUBMITTED: {
      return getFormSessionSubmittedMessage();
      break;
    }

    case APP_ERRORS.GUEST_MODE_DISABLED_ERROR: {
      return <AppErrorPage />;
    }
    case APP_ERRORS.FORM_PENDING: {
      return getFormSessionPendingMessage();
      break;
    }
    case APP_ERRORS.QUOTA_EXCEEDED: {
      // message = getQuotaExceededMessage();
      return <AppErrorPage />;

      break;
    }

    case APP_ERRORS.FORM_NOT_AVAILABLE: {
      return <AppErrorPage />;
      break;
    }
    case APP_ERRORS.USER_ACCOUNT_NOT_APPROVED: {
      return <AppErrorPage />;
      break;
    }
    case APP_ERRORS.USER_ACCOUNT_DISABILITY_CATEGORY_MISMATCH: {
      return <AppErrorPage />;
      break;
    }
    case APP_ERRORS.USER_ACCOUNT_REJECTED: {
      return <AppErrorPage />;
      break;
    }
    default:
      return null;
  }

  return (
    <Card className={cardClass}>
      <section
        dangerouslySetInnerHTML={{
          __html: sanitizer(message),
        }}
      />
    </Card>
  );
};

export default AppError;
